import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Empty, Form, Input, Layout, Modal, Select, Space, Spin, Table, Tag, Typography, notification } from "antd";
import { CopyOutlined, DeleteOutlined, ExportOutlined, EyeInvisibleOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import download from "downloadjs";
import moment from "moment";
import { sessionStore, SESSION_ATTRS } from "../store/session";
import { ACCESS_CREDENTIAL_MODE, USER_STATE } from "../users/constants";
import { getUsers } from "../users/actions";
import { getInceptionUserPermissionGroups } from "../users/serviceIntegrations/inceptionActions";
import WalletCredentialList from "../users/WalletCredentialList";
import { WalletCredential, getWalletCredentialDescription, getWalletPlatformLabel } from "../users/walletCredentials";
import { createAdminAccessDelegation, getAdminAccessDelegations, getConfiguredInceptionTenants, reconcileAdminAccessDelegation, revokeAdminAccessDelegation, updateVisitorWalletPass } from "./actions";
import "./visitorManagement.scss";
import { isTenantVisitorPassDelegationEnabled } from "../utils/tenantFeatures";

const { Parser } = require("json2csv");

const { Content } = Layout;
const { Title, Text } = Typography;

const INCEPTION_CONNECTED = "CONNECTED";
const DEFAULT_ACCESS_HOURS = 8;
const QUICK_ACCESS_HOURS = [2, 8, 24];
const DELEGATION_CREDENTIAL_MODES = [
    ACCESS_CREDENTIAL_MODE.MOBILE_APP,
    ACCESS_CREDENTIAL_MODE.WALLET,
];
const VISITOR_CSV_EXPORT_FIELDS = [
    "Visitor Name",
    "Visitor Email",
    "Delegating User",
    "Tenant",
    "Status",
    "Access Starts At",
    "Access Ends At",
    "Wallet",
    "Card Number",
    "Created At",
    "Updated At",
];

const getStatusColor = (status: string) => {
    switch (status) {
        case "ACTIVE":
        case "WALLET_PROVISIONED":
            return "green";
        case "REVOKED":
        case "FAILED":
        case "EXPIRED":
            return "red";
        case "PROVISIONING":
        case "ISSUANCE_CREATED":
            return "gold";
        default:
            return "blue";
    }
};

const getStatusLabel = (status: string = "") => {
    return status
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const canCopyProvisioningLink = (delegation: any) => {
    if (!delegation?.provisioningUrl) {
        return false;
    }

    return [
        "LINK_CREATED",
        "LINK_SENT",
        "ISSUANCE_CREATED",
        "PROVISIONING",
    ].includes(delegation.status);
};

const formatDate = (value?: string) => {
    if (!value) {
        return "-";
    }
    const date = moment(value);
    return date.isValid() ? date.format("DD MMM YYYY, h:mm a") : "-";
};

const formatPeriod = (validFrom?: string, validTill?: string) => {
    const start = formatDate(validFrom);
    const end = formatDate(validTill);
    return `${start} - ${end}`;
};

const getUserLabel = (user: any) => {
    const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    return name ? `${name} (${user.email})` : user?.email || user?.userId;
};

const formatCsvDate = (value?: string) => {
    if (!value) {
        return "";
    }
    const date = moment(value);
    return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "";
};

const getVisitorName = (delegation: any) =>
    `${delegation?.delegateFirstName || ""} ${delegation?.delegateLastName || ""}`.trim();

const getWalletLabel = (platform?: string) =>
    platform ? `${platform.charAt(0)}${platform.slice(1).toLowerCase()} Wallet` : "";

const getVisitorWalletPasses = (delegation: any): WalletCredential[] =>
    Array.isArray(delegation?.walletPasses) ? delegation.walletPasses : [];

const getVisitorPassStatusSummary = (delegation: any) => {
    const passes = getVisitorWalletPasses(delegation);
    if (!passes.length) {
        return { label: getStatusLabel(delegation.status), detail: undefined, mixed: false };
    }
    const counts = passes.reduce((result: Record<string, number>, pass) => {
        const status = `${pass.status || "UNKNOWN"}`.toUpperCase();
        result[status] = (result[status] || 0) + 1;
        return result;
    }, {});
    const statusParts = Object.entries(counts).map(([status, count]) =>
        `${count} ${getStatusLabel(status).toLowerCase()}`
    );
    return {
        label: Object.keys(counts).length > 1 ? "Mixed" : getStatusLabel(Object.keys(counts)[0]),
        detail: `${passes.length} ${passes.length === 1 ? "pass" : "passes"} · ${statusParts.join(" · ")}`,
        mixed: Object.keys(counts).length > 1,
    };
};

const getSafeFileNamePart = (value?: string) =>
    `${value || "all"}`.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "all";

export const VisitorManagementView = () => {
    const { state } = useContext(sessionStore);
    const loggedUser: any = state.get(SESSION_ATTRS.LOGGED_USER);
    const tenants = state.get(SESSION_ATTRS.TENANTS) as any[] | undefined;
    const visitorPassEnabledTenantIds = useMemo(
        () => new Set((tenants || []).filter(isTenantVisitorPassDelegationEnabled).map((tenant: any) => tenant.tenantId)),
        [tenants]
    );
    const buildingId = loggedUser?.buildingId;

    const [form] = Form.useForm();
    const [configuredTenants, setConfiguredTenants] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [delegations, setDelegations] = useState<any[]>([]);
    const [selectedTenantId, setSelectedTenantId] = useState<string>();
    const [isLoadingTenants, setIsLoadingTenants] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingDelegations, setIsLoadingDelegations] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedOwnerUserId, setSelectedOwnerUserId] = useState<string>();
    const [isLoadingGroups, setIsLoadingGroups] = useState(false);
    const [permissionGroups, setPermissionGroups] = useState<any[]>([]);
    const [assignedPermissionGroupIds, setAssignedPermissionGroupIds] = useState<string[]>([]);
    const [visibleVisitorEmails, setVisibleVisitorEmails] = useState<Set<string>>(new Set());

    const tenantOptions = useMemo(() => configuredTenants.map((tenant) => ({
        label: tenant.name,
        value: tenant.tenantId,
    })), [configuredTenants]);

    const delegatingUsers = useMemo(() => users
        .filter((user) => user.tenantId === selectedTenantId)
        .filter((user) => DELEGATION_CREDENTIAL_MODES.includes(
            user.accessCredentialPolicy?.mode || ACCESS_CREDENTIAL_MODE.MOBILE_APP
        ))
        .filter((user) => user.inceptionConnectionStatus === INCEPTION_CONNECTED || !!user.externalUserId)
        .sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b), undefined, { sensitivity: "base" })),
    [selectedTenantId, users]);

    const selectedOwnerUser = useMemo(
        () => delegatingUsers.find((user) => user.userId === selectedOwnerUserId),
        [delegatingUsers, selectedOwnerUserId]
    );

    const assignedPermissionGroups = useMemo(() => {
        const assigned = new Set(assignedPermissionGroupIds || []);
        return permissionGroups.filter((group) => assigned.has(group.id));
    }, [assignedPermissionGroupIds, permissionGroups]);

    const loadConfiguredTenants = useCallback(async () => {
        if (!buildingId) {
            return;
        }

        try {
            setIsLoadingTenants(true);
            const response = await getConfiguredInceptionTenants(buildingId);
            const configuredTenantList = Array.isArray(response?.tenants) ? response.tenants : [];
            setConfiguredTenants(
                configuredTenantList.filter((tenant: any) => visitorPassEnabledTenantIds.has(tenant.tenantId))
            );
        } catch (error) {
            console.error(error);
            notification.error({ message: "Unable to load Inception tenants" });
        } finally {
            setIsLoadingTenants(false);
        }
    }, [buildingId, visitorPassEnabledTenantIds]);

    const loadUsers = useCallback(async () => {
        if (!buildingId) {
            return;
        }

        try {
            setIsLoadingUsers(true);
            const userList = await getUsers(USER_STATE.ACTIVE, buildingId);
            setUsers(Array.isArray(userList) ? userList : []);
        } catch (error) {
            console.error(error);
            notification.error({ message: "Unable to load users" });
        } finally {
            setIsLoadingUsers(false);
        }
    }, [buildingId]);

    const loadDelegations = useCallback(async () => {
        if (!buildingId) {
            return;
        }

        try {
            setIsLoadingDelegations(true);
            const response = await getAdminAccessDelegations({
                buildingId,
                ...(selectedTenantId ? { tenantId: selectedTenantId } : {}),
            });
            const delegationList = Array.isArray(response?.delegations) ? response.delegations : [];
            setDelegations(
                delegationList.filter((delegation: any) => visitorPassEnabledTenantIds.has(delegation.tenantId))
            );
        } catch (error) {
            console.error(error);
            notification.error({ message: "Unable to load visitor passes" });
        } finally {
            setIsLoadingDelegations(false);
        }
    }, [buildingId, selectedTenantId, visitorPassEnabledTenantIds]);

    useEffect(() => {
        loadConfiguredTenants();
        loadUsers();
    }, [loadConfiguredTenants, loadUsers]);

    useEffect(() => {
        loadDelegations();
    }, [loadDelegations]);

    const resetOwnerSelection = () => {
        setSelectedOwnerUserId(undefined);
        setPermissionGroups([]);
        setAssignedPermissionGroupIds([]);
        form.setFieldsValue({ ownerUserId: undefined });
    };

    const onTenantChanged = (tenantId?: string) => {
        setSelectedTenantId(tenantId);
        resetOwnerSelection();
        form.setFieldsValue({ tenantId });
    };

    const loadPermissionGroupsForUser = async (ownerUserId: string) => {
        const tenant = configuredTenants.find((item) => item.tenantId === form.getFieldValue("tenantId"));
        if (!tenant || !ownerUserId) {
            setPermissionGroups([]);
            setAssignedPermissionGroupIds([]);
            return;
        }

        try {
            setIsLoadingGroups(true);
            const response = await getInceptionUserPermissionGroups(tenant.buildingId, tenant.tenantId, ownerUserId);
            setPermissionGroups(Array.isArray(response?.permissionGroups) ? response.permissionGroups : []);
            setAssignedPermissionGroupIds(
                Array.isArray(response?.assignedPermissionGroupIds) ? response.assignedPermissionGroupIds : []
            );
        } catch (error) {
            console.error(error);
            setPermissionGroups([]);
            setAssignedPermissionGroupIds([]);
            notification.error({ message: "Unable to load user's Inception groups" });
        } finally {
            setIsLoadingGroups(false);
        }
    };

    const openCreateModal = () => {
        const tenantId = selectedTenantId || configuredTenants[0]?.tenantId;
        const validFrom = moment();
        setSelectedTenantId(tenantId);
        setSelectedOwnerUserId(undefined);
        setPermissionGroups([]);
        setAssignedPermissionGroupIds([]);
        form.resetFields();
        form.setFieldsValue({
            tenantId,
            validFrom,
            validTill: validFrom.clone().add(DEFAULT_ACCESS_HOURS, "hours"),
        });
        setIsCreateModalVisible(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalVisible(false);
        setSelectedOwnerUserId(undefined);
        setPermissionGroups([]);
        setAssignedPermissionGroupIds([]);
        form.resetFields();
    };

    const onOwnerChanged = (ownerUserId: string) => {
        setSelectedOwnerUserId(ownerUserId);
        loadPermissionGroupsForUser(ownerUserId);
    };

    const setQuickAccessPeriod = (hours: number) => {
        const validFrom = form.getFieldValue("validFrom") || moment();
        form.setFieldsValue({
            validFrom,
            validTill: validFrom.clone().add(hours, "hours"),
        });
    };

    const onCreateDelegation = async (values: any) => {
        const tenant = configuredTenants.find((item) => item.tenantId === values.tenantId);
        if (!tenant) {
            notification.error({ message: "Select a tenant with Inception configured" });
            return;
        }
        if (values.validTill?.isSameOrBefore(values.validFrom)) {
            notification.error({ message: "End time must be after start time" });
            return;
        }

        try {
            setIsSaving(true);
            await createAdminAccessDelegation({
                buildingId: tenant.buildingId,
                tenantId: tenant.tenantId,
                ownerUserId: values.ownerUserId,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                validFrom: values.validFrom.toISOString(),
                validTill: values.validTill.toISOString(),
            });
            notification.success({ message: "Visitor pass created" });
            closeCreateModal();
            await loadDelegations();
        } catch (error: any) {
            console.error(error);
            notification.error({ message: error?.message || "Unable to create visitor pass" });
        } finally {
            setIsSaving(false);
        }
    };

    const onRevokeDelegation = (delegation: any) => {
        const passCount = getVisitorWalletPasses(delegation).length;
        Modal.confirm({
            title: "Revoke all visitor credentials",
            content: `Revoke access for ${delegation.delegateFirstName} ${delegation.delegateLastName}? This will reconcile HID and revoke ${passCount || "every discovered"} Wallet ${passCount === 1 ? "pass" : "passes"}.`,
            okText: "Revoke all",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    try {
                        await revokeAdminAccessDelegation(delegation);
                        notification.success({ message: "All visitor credentials revoked" });
                    } finally {
                        await loadDelegations();
                    }
                } catch (error) {
                    console.error(error);
                    notification.error({ message: "Unable to revoke visitor pass" });
                }
            },
        });
    };

    const onVisitorWalletPassAction = (
        delegation: any,
        credential: WalletCredential,
        action: "suspend" | "resume" | "revoke"
    ) => {
        if (!credential.passId) {
            notification.error({ message: "This visitor pass is waiting for its HID identity" });
            return;
        }
        const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
        const deviceLabel = getWalletPlatformLabel(credential.platform, credential.deviceType);
        Modal.confirm({
            title: `${actionLabel} ${deviceLabel}`,
            content: `${actionLabel} only ${getWalletCredentialDescription(credential)}? Other visitor Wallet passes will remain unchanged.`,
            okText: actionLabel,
            okButtonProps: { danger: action === "revoke" },
            onOk: async () => {
                try {
                    await updateVisitorWalletPass(credential.passId!, action);
                    notification.success({ message: `${deviceLabel} ${action} requested` });
                } catch (error) {
                    console.error(error);
                    notification.error({ message: `Unable to ${action} ${deviceLabel}` });
                } finally {
                    await loadDelegations();
                }
            },
        });
    };

    const onReconcileDelegation = async (delegation: any) => {
        try {
            await reconcileAdminAccessDelegation(delegation);
            notification.success({ message: "Visitor Wallet passes synchronized" });
        } catch (error) {
            console.error(error);
            notification.error({ message: "Unable to synchronize visitor Wallet passes" });
        } finally {
            await loadDelegations();
        }
    };

    const copyProvisioningLink = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            notification.success({ message: "Provisioning link copied" });
        } catch (error) {
            notification.error({ message: "Unable to copy provisioning link" });
        }
    };

    const exportVisitorsToCsv = () => {
        try {
            if (!delegations.length) {
                notification.info({ message: "No visitor details available to export" });
                return;
            }

            const selectedTenant = configuredTenants.find((tenant) => tenant.tenantId === selectedTenantId);
            const exportableVisitors = delegations.map((delegation) => {
                const tenantName = configuredTenants.find((tenant) => tenant.tenantId === delegation.tenantId)?.name ||
                    delegation.tenantId ||
                    "";

                return {
                    "Visitor Name": getVisitorName(delegation),
                    "Visitor Email": delegation.delegateEmail || "",
                    "Delegating User": delegation.ownerName || delegation.ownerUserId || "",
                    "Tenant": tenantName,
                    "Status": getVisitorPassStatusSummary(delegation).label,
                    "Access Starts At": formatCsvDate(delegation.validFrom || delegation.createdAt),
                    "Access Ends At": formatCsvDate(delegation.validTill),
                    "Wallet": getVisitorWalletPasses(delegation).length
                        ? getVisitorWalletPasses(delegation)
                            .map((pass) => getWalletPlatformLabel(pass.platform, pass.deviceType))
                            .join("; ")
                        : getWalletLabel(delegation.platform),
                    "Card Number": getVisitorWalletPasses(delegation).length
                        ? getVisitorWalletPasses(delegation)
                            .map((pass) => pass.cardNumber)
                            .filter(Boolean)
                            .join("; ")
                        : delegation.cardNumber || "",
                    "Created At": formatCsvDate(delegation.createdAt),
                    "Updated At": formatCsvDate(delegation.updatedAt),
                };
            });
            const parser = new Parser({ fields: VISITOR_CSV_EXPORT_FIELDS });
            const csv = parser.parse(exportableVisitors);
            const file = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
            const tenantPart = getSafeFileNamePart(selectedTenant?.name || selectedTenantId || "all-tenants");
            const datePart = moment().format("YYYY-MM-DD");
            download(file, `Visitor-Details-${tenantPart}-${datePart}.csv`);
        } catch (error) {
            console.error(error);
            notification.error({ message: "Unable to export visitor details" });
        }
    };

    const toggleVisitorEmail = (delegationId: string) => {
        setVisibleVisitorEmails((current) => {
            const next = new Set(current);
            if (next.has(delegationId)) {
                next.delete(delegationId);
            } else {
                next.add(delegationId);
            }
            return next;
        });
    };

    const columns = [
        {
            title: "Visitor",
            key: "visitor",
            render: (_: any, row: any) => {
                const isEmailVisible = visibleVisitorEmails.has(row.delegationId);
                return (
                    <div>
                        <div className="visitor-name">{`${row.delegateFirstName || ""} ${row.delegateLastName || ""}`.trim() || "-"}</div>
                        <div className="visitor-email-row">
                            <Text type="secondary">{isEmailVisible ? row.delegateEmail : "Email hidden"}</Text>
                            <Button
                                type="link"
                                size="small"
                                className="visitor-email-toggle"
                                icon={isEmailVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                onClick={() => toggleVisitorEmail(row.delegationId)}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Delegating User",
            dataIndex: "ownerName",
            key: "ownerName",
            render: (ownerName: string) => ownerName || "-",
        },
        {
            title: "Tenant",
            dataIndex: "tenantId",
            key: "tenantId",
            render: (tenantId: string) => configuredTenants.find((tenant) => tenant.tenantId === tenantId)?.name || tenantId,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: string, row: any) => {
                const summary = getVisitorPassStatusSummary(row);
                return (
                    <div className="visitor-status-summary">
                        <Tag color={summary.mixed ? "blue" : getStatusColor(status)}>{summary.label}</Tag>
                        {summary.detail && <Text type="secondary">{summary.detail}</Text>}
                    </div>
                );
            },
        },
        {
            title: "Access Period",
            key: "accessPeriod",
            render: (_: any, row: any) => formatPeriod(row.validFrom || row.createdAt, row.validTill),
        },
        {
            title: "Wallet",
            key: "platform",
            width: 280,
            render: (_: any, row: any) => {
                const walletPasses = getVisitorWalletPasses(row);
                if (!walletPasses.length) {
                    return (
                        <div>
                            <div>{getWalletLabel(row.platform) || "-"}</div>
                            {row.cardNumber && <Text type="secondary">Card {row.cardNumber}</Text>}
                        </div>
                    );
                }
                return (
                    <WalletCredentialList
                        credentials={walletPasses}
                        actionsEnabled
                        maxVisible={2}
                        onSuspend={(credential) => onVisitorWalletPassAction(row, credential, "suspend")}
                        onResume={(credential) => onVisitorWalletPassAction(row, credential, "resume")}
                        onRevoke={(credential) => onVisitorWalletPassAction(row, credential, "revoke")}
                    />
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            render: (_: any, row: any) => {
                const hasRevocablePass = getVisitorWalletPasses(row).some((pass) => [
                    "ISSUANCE_CREATED",
                    "PROVISIONING",
                    "ACTIVE",
                    "SUSPENDED",
                ].includes(`${pass.status || ""}`.toUpperCase()));
                const canRevoke = hasRevocablePass || (row.status !== "REVOKED" && row.status !== "EXPIRED");
                const showCopyLink = canCopyProvisioningLink(row);
                const canReconcile = !!row.hidWalletUserId;
                if (!showCopyLink && !canRevoke && !canReconcile) {
                    return <div className="visitor-actions-empty"><Text type="secondary">-</Text></div>;
                }

                return (
                    <Space wrap size={6}>
                        {showCopyLink && (
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => copyProvisioningLink(row.provisioningUrl)}
                            >
                                Copy link
                            </Button>
                        )}
                        {canReconcile && (
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={() => onReconcileDelegation(row)}
                            >
                                Sync passes
                            </Button>
                        )}
                        {canRevoke && (
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => onRevokeDelegation(row)}
                            >
                                Revoke all
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    const renderGroupPreview = () => {
        if (!selectedOwnerUser) {
            return null;
        }

        return (
            <div className="visitor-groups-preview">
                <Text strong>Delegating user's Inception groups</Text>
                {isLoadingGroups ? (
                    <div className="visitor-groups-loading"><Spin size="small" /> Loading groups...</div>
                ) : assignedPermissionGroups.length ? (
                    <div className="visitor-groups-tags">
                        {assignedPermissionGroups.map((group) => <Tag key={group.id}>{group.name || group.id}</Tag>)}
                    </div>
                ) : (
                    <Text type="secondary">No Inception groups assigned.</Text>
                )}
            </div>
        );
    };

    return (
        <Content className="visitor-management">
            <div className="visitor-management-header">
                <div>
                    <Title level={3}>Visitor Management</Title>
                </div>
                <Space>
                    <Button
                        icon={<ExportOutlined />}
                        onClick={exportVisitorsToCsv}
                        disabled={!delegations.length}
                    >
                        Export CSV
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadDelegations} loading={isLoadingDelegations}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} disabled={!configuredTenants.length}>
                        Create Visitor Pass
                    </Button>
                </Space>
            </div>

            <div className="visitor-management-toolbar">
                <div className="visitor-filter">
                    <Select
                        placeholder="Select Tenant"
                        loading={isLoadingTenants}
                        options={tenantOptions}
                        value={selectedTenantId}
                        onChange={onTenantChanged}
                        allowClear
                    />
                </div>
            </div>

            {!isLoadingTenants && !configuredTenants.length ? (
                <div className="visitor-empty">
                    <Empty description="No tenants with Inception configured are available for this building." />
                </div>
            ) : (
                <Table
                    rowKey="delegationId"
                    loading={isLoadingDelegations || isLoadingUsers}
                    columns={columns}
                    dataSource={delegations}
                    pagination={{ pageSize: 10 }}
                />
            )}

            <Modal
                title="Create visitor pass"
                visible={isCreateModalVisible}
                onCancel={closeCreateModal}
                footer={null}
                destroyOnClose
                className="visitor-create-modal"
            >
                <Form form={form} layout="vertical" onFinish={onCreateDelegation}>
                    <Form.Item
                        label="Tenant"
                        name="tenantId"
                        rules={[{ required: true, message: "Please select a tenant" }]}
                    >
                        <Select
                            placeholder="Select tenant"
                            options={tenantOptions}
                            onChange={onTenantChanged}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Delegating user"
                        name="ownerUserId"
                        extra="Select the user whose access will be delegated to the visitor."
                        rules={[{ required: true, message: "Please select a delegating user" }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select delegating user"
                            loading={isLoadingUsers}
                            optionFilterProp="label"
                            notFoundContent={isLoadingUsers ? <Spin size="small" /> : "No enabled delegating users for this tenant"}
                            options={delegatingUsers.map((user) => ({
                                label: getUserLabel(user),
                                value: user.userId,
                            }))}
                            onChange={onOwnerChanged}
                        />
                    </Form.Item>

                    {renderGroupPreview()}

                    <div className="visitor-form-row">
                        <Form.Item
                            label="First name"
                            name="firstName"
                            rules={[{ required: true, message: "Please enter visitor first name" }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Last name"
                            name="lastName"
                            rules={[{ required: true, message: "Please enter visitor last name" }]}
                        >
                            <Input />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Email address"
                        name="email"
                        rules={[
                            { required: true, message: "Please enter visitor email" },
                            { type: "email", message: "Please enter a valid email address" },
                        ]}
                    >
                        <Input placeholder="visitor@example.com" />
                    </Form.Item>

                    <Form.Item label="Quick duration from start time">
                        <Space wrap>
                            {QUICK_ACCESS_HOURS.map((hours) => (
                                <Button key={hours} onClick={() => setQuickAccessPeriod(hours)}>
                                    {hours === 24 ? "24 hours" : `${hours} hours`}
                                </Button>
                            ))}
                        </Space>
                    </Form.Item>

                    <div className="visitor-form-row">
                        <Form.Item
                            label="Start time"
                            name="validFrom"
                            rules={[{ required: true, message: "Please select start date and time" }]}
                        >
                            <DatePicker
                                showTime
                                format="DD MMM YYYY h:mm a"
                                disabledDate={(date) => !!date && date.isBefore(moment().startOf("day"))}
                                style={{ width: "100%" }}
                                onChange={(validFrom) => {
                                    if (!validFrom) {
                                        return;
                                    }
                                    const validTill = form.getFieldValue("validTill");
                                    if (!validTill || validTill.isSameOrBefore(validFrom)) {
                                        form.setFieldsValue({
                                            validTill: validFrom.clone().add(DEFAULT_ACCESS_HOURS, "hours"),
                                        });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="End time"
                            name="validTill"
                            rules={[{ required: true, message: "Please select end date and time" }]}
                        >
                            <DatePicker
                                showTime
                                format="DD MMM YYYY h:mm a"
                                disabledDate={(date) => !!date && date.isBefore(moment().startOf("day"))}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </div>

                    <div className="visitor-modal-actions">
                        <Button onClick={closeCreateModal}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={isSaving}>
                            Send Invitation
                        </Button>
                    </div>
                </Form>
            </Modal>
        </Content>
    );
};
